import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  updatePassword,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { auth, db, googleProvider } from '../config/firebase';
import { getOrInitializeAccount } from '../utils/accountUtils';
import { upsertUserProfile } from '../services/firestoreUserProfile';
import { doc, onSnapshot } from 'firebase/firestore';

// Auth context type
interface AuthContextType {
  user: User | null;
  loading: boolean;
  approvalStatus: 'pending' | 'approved' | null;
  error: string | null;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (displayName: string, photoURL?: string) => Promise<void>;
  updateUserEmail: (newEmail: string, currentPassword: string) => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Listen for auth state changes and handle redirect result
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user?.uid) {
        getOrInitializeAccount(user.uid).catch((err) => {
          console.error('Failed to initialize Firestore account data:', err);
        });
        // Keep a basic user profile document in Firestore.
        upsertUserProfile(user).catch((err) => {
          console.error('Failed to upsert Firestore user profile:', err);
        });
      }
      setLoading(false);
    });

    // Check for redirect result (for Google sign-in redirect flow)
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          setUser(result.user);
          if (result.user.uid) {
            getOrInitializeAccount(result.user.uid).catch((err) => {
              console.error('Failed to initialize Firestore account data:', err);
            });
            upsertUserProfile(result.user).catch((err) => {
              console.error('Failed to upsert Firestore user profile:', err);
            });
          }
        }
      })
      .catch((err) => {
        console.error('Redirect result error:', err);
        setError(getFirebaseErrorMessage(err.code));
      });

    return () => unsubscribe();
  }, []);

  // Watch approval status in real-time so the UI unlocks immediately when approved
  useEffect(() => {
    if (!user?.uid) {
      setApprovalStatus(null);
      return;
    }
    const ref = doc(db, 'users', user.uid);

    const fallbackTimer = setTimeout(() => {
      setApprovalStatus((current) => (current === null ? 'approved' : current));
    }, 5000);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) return;
        clearTimeout(fallbackTimer);
        const status = snapshot.data()?.status;
        setApprovalStatus(status === 'pending' ? 'pending' : 'approved');
      },
      (_error) => {
        clearTimeout(fallbackTimer);
        setApprovalStatus('approved');
      }
    );

    return () => {
      clearTimeout(fallbackTimer);
      unsubscribe();
    };
  }, [user?.uid]);

  // Clear error
  const clearError = () => setError(null);

  // Sign in with email and password
  const signInWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      setError(null);
      setLoading(true);
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      if (user?.uid) {
        await getOrInitializeAccount(user.uid);
        await upsertUserProfile(user);
      }

      // Update display name if provided
      if (displayName && user) {
        await updateProfile(user, { displayName });
      }
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);

      // Try popup first
      try {
        await signInWithPopup(auth, googleProvider);
      } catch (popupError: any) {
        // If popup is blocked or fails, try redirect
        if (popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.code === 'auth/cancelled-popup-request') {
          await signInWithRedirect(auth, googleProvider);
          return; // Will complete on redirect
        }
        throw popupError;
      }
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    }
  };

  // Update user profile
  const updateUserProfile = async (displayName: string, photoURL?: string) => {
    try {
      setError(null);
      if (user) {
        await updateProfile(user, { displayName, photoURL: photoURL || user.photoURL });
        // Force refresh user state
        setUser({ ...user });
      }
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    }
  };

  // Re-authenticate user (needed for sensitive operations)
  const reauthenticate = async (password: string) => {
    if (!user || !user.email) throw new Error('No user logged in');
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  };

  // Update user email
  const updateUserEmail = async (newEmail: string, currentPassword: string) => {
    try {
      setError(null);
      if (user) {
        await reauthenticate(currentPassword);
        await updateEmail(user, newEmail);
      }
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    }
  };

  // Update user password
  const updateUserPassword = async (currentPassword: string, newPassword: string) => {
    try {
      setError(null);
      if (user) {
        await reauthenticate(currentPassword);
        await updatePassword(user, newPassword);
      }
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    }
  };

  // Delete account
  const deleteAccount = async (password: string) => {
    try {
      setError(null);
      if (user) {
        // For Google users, password might not be needed
        if (user.providerData[0]?.providerId !== 'google.com') {
          await reauthenticate(password);
        }
        await deleteUser(user);
      }
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.code));
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    approvalStatus,
    error,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateUserProfile,
    updateUserEmail,
    updateUserPassword,
    deleteAccount,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to get user-friendly error messages
const getFirebaseErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.';
    case 'auth/popup-blocked':
      return 'Popup was blocked. Please allow popups or try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    case 'auth/requires-recent-login':
      return 'Please sign in again to complete this action.';
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/operation-not-allowed':
      return 'Google Sign-In is not enabled. Please enable it in Firebase Console (Authentication > Sign-in method > Google).';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for Google Sign-In. Add it in Firebase Console.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with the same email but different sign-in method.';
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/internal-error':
      return 'An internal error occurred. Please try again later.';
    default:
      console.error('Unknown Firebase error code:', errorCode);
      return `An error occurred (${errorCode}). Please try again.`;
  }
};

export default AuthContext;
